function esc(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function formatClp(amount) {
    const n = Number(amount) || 0;
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0,
    }).format(n);
}

const UNIDADES = ['', 'un', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
const DIEZ_A_DIECINUEVE = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
const DECENAS = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

function chunkToWords(n) {
    if (n === 0) return '';
    if (n === 100) return 'cien';
    const c = Math.floor(n / 100);
    const d = Math.floor((n % 100) / 10);
    const u = n % 10;
    const parts = [];
    if (c) parts.push(CENTENAS[c]);
    const rest = n % 100;
    if (rest >= 10 && rest < 20) parts.push(DIEZ_A_DIECINUEVE[rest - 10]);
    else {
        if (d === 2 && u > 0) parts.push(`veinti${UNIDADES[u]}`);
        else {
            if (d) parts.push(DECENAS[d]);
            if (u) parts.push(UNIDADES[u]);
        }
    }
    return parts.filter(Boolean).join(' ');
}

export function numeroAPalabrasClp(value) {
    const n = Math.round(Math.abs(Number(value) || 0));
    if (n === 0) return 'cero pesos';
    const millones = Math.floor(n / 1_000_000);
    const miles = Math.floor((n % 1_000_000) / 1000);
    const resto = n % 1000;
    const parts = [];
    if (millones) {
        parts.push(millones === 1 ? 'un millón' : `${chunkToWords(millones)} millones`);
    }
    if (miles) {
        parts.push(miles === 1 ? 'mil' : `${chunkToWords(miles)} mil`);
    }
    if (resto) parts.push(chunkToWords(resto));
    const texto = parts.join(' ').replace(/\s+/g, ' ').trim();
    return `${texto} pesos`.toUpperCase();
}

function periodoLabel(periodo) {
    if (!periodo) return '';
    const [y, m] = String(periodo).split('-');
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${months[parseInt(m, 10) - 1] || m} ${y}`;
}

/**
 * Print an A4 Chilean-style liquidación de sueldo.
 */
export function printLiquidacion(liq) {
    const printWindow = window.open('', '_blank', 'width=900,height=1100');
    if (!printWindow) return false;

    const snap = liq.parametros_snapshot || {};
    const haberes = liq.haberes || [];
    const descuentos = liq.descuentos || [];
    const maxRows = Math.max(haberes.length, descuentos.length, 4);
    const rows = [];
    for (let i = 0; i < maxRows; i++) {
        const h = haberes[i];
        const d = descuentos[i];
        rows.push(`<tr>
            <td>${h ? esc(h.nombre) : ''}</td>
            <td class="num">${h ? formatClp(h.monto) : ''}</td>
            <td>${d ? esc(d.nombre) : ''}</td>
            <td class="num">${d ? formatClp(d.monto) : ''}</td>
        </tr>`);
    }

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Liquidación ${esc(liq.periodo)} — ${esc(liq.colaborador_nombre)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1c1917; font-size: 12px; }
  h1 { font-size: 16px; margin: 0 0 4px; letter-spacing: 0.04em; text-transform: uppercase; }
  .muted { color: #57534e; }
  .header { display: flex; justify-content: space-between; border-bottom: 2px solid #1c1917; padding-bottom: 10px; margin-bottom: 14px; }
  .brand { font-weight: 700; font-size: 18px; }
  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin-bottom: 14px; }
  .box { border: 1px solid #d6d3d1; border-radius: 6px; padding: 8px 10px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 6px 8px; border-bottom: 1px solid #e7e5e4; vertical-align: top; }
  th { background: #f5f5f4; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
  td.num, th.num { text-align: right; white-space: nowrap; }
  .totals td { font-weight: 700; border-top: 2px solid #1c1917; }
  .liquido { margin-top: 16px; border: 2px solid #1c1917; padding: 10px 12px; }
  .liquido strong { font-size: 18px; }
  .words { margin-top: 6px; font-size: 11px; text-transform: uppercase; }
  .sign { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; margin-top: 48px; text-align: center; }
  .sign .line { border-top: 1px solid #1c1917; padding-top: 6px; margin-top: 48px; }
  .foot { margin-top: 20px; font-size: 10px; color: #78716c; }
</style></head><body>
  <div class="header">
    <div>
      <div class="brand">${esc(snap.razon_social || 'Super Colchagua')}</div>
      <div class="muted">${esc(snap.rut_empleador || '')}</div>
      <div class="muted">${esc(snap.direccion_empleador || '')}</div>
    </div>
    <div style="text-align:right">
      <h1>Liquidación de sueldo</h1>
      <div><strong>${esc(periodoLabel(liq.periodo))}</strong></div>
      <div class="muted">${esc(liq.fecha_desde)} al ${esc(liq.fecha_hasta)}</div>
    </div>
  </div>
  <div class="meta">
    <div class="box"><div class="muted">Trabajador</div><strong>${esc(liq.colaborador_nombre)}</strong></div>
    <div class="box"><div class="muted">RUT</div><strong>${esc(liq.colaborador_rut || '—')}</strong></div>
    <div class="box"><div class="muted">Cargo</div><strong>${esc(liq.cargo || liq.rol_nombre || '—')}</strong></div>
    <div class="box"><div class="muted">Contrato</div><strong>${esc(liq.tipo_contrato || 'indefinido')}</strong></div>
    <div class="box"><div class="muted">Días trabajados</div><strong>${esc(liq.dias_trabajados)} / ${esc(liq.dias_mes || 30)}</strong></div>
    <div class="box"><div class="muted">Horas extras</div><strong>${esc(liq.horas_extras || 0)} h</strong></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Haberes</th><th class="num">Monto</th>
        <th>Descuentos</th><th class="num">Monto</th>
      </tr>
    </thead>
    <tbody>
      ${rows.join('')}
      <tr class="totals">
        <td>Total haberes</td><td class="num">${formatClp(liq.total_haberes)}</td>
        <td>Total descuentos</td><td class="num">${formatClp(liq.total_descuentos)}</td>
      </tr>
    </tbody>
  </table>
  <div class="liquido">
    <div class="muted">Líquido a pagar</div>
    <strong>${formatClp(liq.liquido)}</strong>
    <div class="words">Son: ${esc(numeroAPalabrasClp(liq.liquido))}</div>
  </div>
  <div class="sign">
    <div><div class="line">Empleador</div></div>
    <div><div class="line">Trabajador (recepción conforme)</div></div>
  </div>
  <p class="foot">Documento interno del ERP. Cotizaciones según parámetros IMM ${esc(snap.imm || '')} / UTM ${esc(snap.utm || '')}. No reemplaza la declaración Previred.</p>
  <script>window.onload=function(){window.print();}</script>
</body></html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    return true;
}
